import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { TShow } from '@schemas'

type CurrentShowContextValue = {
  currentShow: TShow | null
  setCurrentShow: (show: TShow | null) => void
  clearCurrentShow: () => void
}

const CurrentShowContext = createContext<CurrentShowContextValue | undefined>(undefined)

function isShowLike(value: unknown): value is TShow {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<TShow>
  return typeof candidate.name === 'string' && typeof candidate.orgId === 'string'
}

function readBootstrappedShow(): TShow | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = window.sessionStorage.getItem('__testCurrentShow__')
    if (!stored) return null
    const parsed = JSON.parse(stored)
    return isShowLike(parsed) ? (parsed as TShow) : null
  } catch {
    return null
  }
}

export function CurrentShowProvider({ children }: { children: React.ReactNode }) {
  const [currentShow, setCurrentShowState] = useState<TShow | null>(() => readBootstrappedShow())

  const setCurrentShow = useCallback((show: TShow | null) => {
    setCurrentShowState(show ? { ...show } : null)
  }, [])

  const clearCurrentShow = useCallback(() => {
    setCurrentShowState(null)
  }, [])

  const value = useMemo(
    () => ({ currentShow, setCurrentShow, clearCurrentShow }),
    [currentShow, setCurrentShow, clearCurrentShow]
  )

  return <CurrentShowContext.Provider value={value}>{children}</CurrentShowContext.Provider>
}

export function useCurrentShow() {
  const ctx = useContext(CurrentShowContext)
  if (!ctx) {
    throw new Error('useCurrentShow must be used within CurrentShowProvider')
  }
  return ctx
}
